import { NumberAbbreviationEnum } from '../constants/enums';

const round = (num: number, precision = 0) => {
  if (precision < 0) {
    return num;
  }
  const x = 10 ** precision;
  return Math.round((num + Number.EPSILON) * x) / x;
};

const countDecimals = (number: number): number => {
  const decimals = Number.isInteger(number)
    ? 0
    : number.toString().split('.')[1]?.length || 0;
  const scientificNotation = number.toExponential().split(/[eE]/);
  if (scientificNotation.length === 2) {
    const exponent = Math.abs(+scientificNotation[1]);
    if (exponent > decimals) {
      return exponent;
    }
  }
  return decimals;
};

export const getPrecision = (
  number: number,
  level: 'high' | 'low' = 'high'
): number => {
  const absNumber = Math.abs(number);
  let precision = 0;
  if (absNumber >= 1000) {
    precision = 0;
  } else if (absNumber >= 10) {
    precision = 2;
  } else if (absNumber >= 1) {
    precision = level === 'high' ? 5 : 2;
  } else if (absNumber >= 0.01) {
    precision = level === 'high' ? 5 : 3;
  } else if (absNumber >= 0.001) {
    precision = level === 'high' ? 6 : 4;
  } else if (absNumber >= 0.0001) {
    precision = level === 'high' ? 7 : 5;
  } else if (absNumber >= 0.00001) {
    precision = level === 'high' ? 9 : 6;
  } else if (absNumber >= 0.000001) {
    precision = 11;
  } else {
    precision = Math.max(2, countDecimals(+absNumber));
  }

  return precision;
};

export const numberFormat = (
  number: number | string,
  decimals = -1,
  decimalSeparator = '.',
  thousandSeparator = ','
): string => {
  const n = !isFinite(+number) ? 0 : +number;

  const precision =
    decimals === -1 ? getPrecision(n) : Math.trunc(Math.abs(decimals));

  let [int = '', dec = ''] = n.toFixed(precision).split('.');

  const formattedInt = int.replace(
    /\B(?=(?:\d{3})+(?!\d))/g,
    thousandSeparator
  );

  const formattedDec = dec ? dec.padEnd(precision, '0') : '';

  //console.log({ int, dec, precision });

  return formattedDec
    ? `${formattedInt}${decimalSeparator}${formattedDec}`
    : `${formattedInt}`;
};

export const numberOutput = (
  number: string | number,
  decimals = -1
): number => {
  return +numberFormat(number, decimals, '.', '');
};

export const numberPrettify = (
  number: number,
  symbol: string = '',
  threshold = NumberAbbreviationEnum.B,
  fixedPrecision: number = -1
): {
  rawValue: number;
  value: number;
  formattedValue: number;
  precision: number;
  suffix: string;
  toString: () => string;
} => {
  const THRESHOLDS = {
    K: 10 ** 3,
    M: 10 ** 6,
    B: 10 ** 9,
    T: 10 ** 12,
    U: 10 ** 18,
  };

  let suffix = symbol ? ` ${symbol}` : '';
  let precision = fixedPrecision;
  let value = number;

  if (number < 1 && number > 0) {
    value = numberOutput(number);
  } else {
    const absNumber = Math.abs(number);

    const currentThreshold = THRESHOLDS[threshold];

    Object.entries(THRESHOLDS).forEach(([k, t]) => {
      if (absNumber >= t && absNumber >= currentThreshold) {
        precision = 1;
        suffix = `${k} ${symbol}`;
        value = round(number / t, precision);
        precision = value % 1 === 0 ? 0 : 1;
      }
    });
  }

  const toString = (): string => {
    return `${numberFormat(value, precision, '.', ',')}${suffix}`;
  };
  return {
    rawValue: number,
    value: numberOutput(number, -1),
    formattedValue: numberOutput(value, precision),
    suffix,
    precision,
    toString,
  };
};
